// The cue. It always says what happens now. Its die is the roll button when
// it is your roll, and a small record of the last roll the rest of the time. Each
// layout puts it last: at the foot of the panel, or at the foot of the screen.

import { ROLL_BUTTON_LABEL } from '../constants';
import { ENERGY_DIE, ENERGY_DIE_LABELS, WHEEL_DIE_LABELS } from '../content/casters';
import { KIND_NAMES } from '../engine/abilities';
import { OWNER_NAMES } from '../engine/describe';
import { squareName } from '../engine/squares';
import type { Move } from '../engine/types';
import { type GameState, type RollRecord, sideTurnNumber } from '../game/state';
import { el } from '../lib/dom';
import { toRoman } from '../lib/roman';
import { spellById } from '../spells/library';
import { canTakeBack } from '../state/castChoice';
import { beatStore } from '../state/director';
import { cancelCast, playMove, rollDie } from '../state/game';
import { stageStore } from '../state/stage';
import { PITCH_KINDS, type PitchKind, suggestionStore } from '../state/suggestion';
import { currentCue, subscribeView, viewedGame } from '../state/view';
import { createPitchIcon } from './pitchIcon';
import { createRollDie } from './RollDie';
import { createSideMark } from './SideMark';

/** Plain words, not final copy. */
const BACK_LABEL = 'Back';
/** A numeral of this many letters or more takes a smaller size. */
const TURN_LONG_LETTERS = 4;

/** What the die gave. Plain words, not final copy. In Godsmode a face is energy or a scroll. */
const landedLine = (roll: RollRecord): string => {
  if (roll.gain === null) return `Cast a ${roll.face + 1}`;
  if (roll.spellId !== null) return `A scroll: ${spellById(roll.spellId).name}`;
  const face = ENERGY_DIE[roll.face];
  if (face === 'scroll') return 'A scroll, with no open slot';
  // The pieces of the side hold the energy. A roll for more than they have room for gives less.
  return face !== undefined && roll.gain < face
    ? `${roll.gain} energy, no room for more`
    : `${roll.gain} energy`;
};

/**
 * The words on the button of each pitch. "Careful" and "Aggro" are Rob's
 * words for the two, 2026-10-13. The pitch of suggest-best has the name of
 * its card.
 */
const pitchLabel = (kind: PitchKind): string =>
  kind === 'deep' ? spellById('suggest-best').name : kind === 'careful' ? 'Careful' : 'Aggro';

/** The move of a pitch in words, for the tooltip of its button. Plain words, not final copy. */
const pitchTip = (state: GameState, kind: PitchKind, move: Move): string => {
  const piece = state.board[move.from];
  const name = piece ? KIND_NAMES[piece.kind] : 'piece';
  return `${pitchLabel(kind)}: ${name} from ${squareName(move.from)} to ${squareName(move.to)}.`;
};

export const createCueBar = (): HTMLElement => {
  // The button is the die itself. Its name is for a screen reader. The headline says "cast".
  const die = createRollDie();
  const dieButton = el('button', {
    className: 'cue__die',
    type: 'button',
    ariaLabel: ROLL_BUTTON_LABEL,
    onclick: rollDie,
  });
  dieButton.appendChild(die.svg);
  // A touch has no hover, so only a mouse makes the die turn.
  dieButton.addEventListener('pointerenter', (event) => {
    if (event.pointerType === 'mouse') die.excite(true);
  });
  dieButton.addEventListener('pointerleave', () => die.excite(false));

  const headline = el('h2', { className: 'cue__headline' });
  const detail = el('p', { className: 'cue__detail' });
  // One button for each pitch the player has. It plays the move of the pitch, in place of a
  // pick on the board. Its color is the color of the marks of the pitch.
  const pitchButtons = PITCH_KINDS.map((kind) => {
    const button = el('button', {
      className: `button cue__pass cue__pitch cue__pitch--${kind}`,
      type: 'button',
      hidden: true,
      onclick: () => {
        const move = suggestionStore.get()[kind];
        if (move) playMove(move);
      },
    });
    // The icon of the pitch is before its word, as on the badge of the piece that gives it.
    if (kind !== 'deep') button.appendChild(createPitchIcon(kind, 'cue__pitch-icon'));
    button.appendChild(document.createTextNode(pitchLabel(kind)));
    return { kind, button };
  });
  // While a cast of this screen waits for its picks, Back takes it back. Nothing was paid yet.
  const backButton = el('button', {
    className: 'button button--pass-through cue__pass cue__back',
    type: 'button',
    textContent: BACK_LABEL,
    hidden: true,
    onclick: cancelCast,
  });
  const actions = el('div', { className: 'cue__actions' }, [
    ...pitchButtons.map(({ button }) => button),
    backButton,
  ]);
  const words = el('div', { className: 'cue__words' }, [headline, detail, actions]);
  const side = el('span', { className: 'cue__side' });
  const aside = el('div', { className: 'cue__aside' }, [side]);
  // The turn of the side to move, as a Roman numeral, on the corner.
  const turnNumber = el('span', { className: 'cue__turn' });
  const bar = el('section', { className: 'cue', ariaLive: 'polite' }, [
    dieButton,
    words,
    aside,
    turnNumber,
  ]);

  const draw = (): void => {
    const state = viewedGame();
    die.label(state.mode === 'gods' ? ENERGY_DIE_LABELS : WHEEL_DIE_LABELS);
    const stage = stageStore.get();
    const cue = currentCue();
    // An earlier step has the floor over the stage and the beats.
    const reviewing = cue.kind === 'review';
    // The final card is on the stage when the match is over. That is not a roll.
    const rolling = !reviewing && stage.phase !== 'hidden' && stage.phase !== 'over';
    const beat = reviewing ? null : beatStore.get();

    // While the die tumbles, the bar does not take the color of the spell. The color lands with the die.
    const tumbling = rolling && stage.phase === 'spinning';
    const polarity = tumbling ? null : cue.polarity;
    bar.className = `cue cue--${cue.kind} cue--turn-${state.turn}${cue.mine ? ' cue--mine' : ''}${polarity ? ` cue--${polarity}` : ''}${cue.danger ? ' cue--danger' : ''}${beat ? ' cue--beat' : ''}`;

    // A match that is over has no turn. The final card and the headline say so.
    const numeral = toRoman(sideTurnNumber(state));
    turnNumber.hidden = state.phase === 'over';
    turnNumber.textContent = numeral;
    turnNumber.ariaLabel = `Turn ${sideTurnNumber(state)}`;
    turnNumber.classList.toggle('cue__turn--long', numeral.length >= TURN_LONG_LETTERS);

    if (rolling && stage.roll) {
      headline.textContent = stage.phase === 'spinning' ? 'Casting' : landedLine(stage.roll);
      detail.textContent = '';
    } else if (beat) {
      // A beat has the floor. It narrates a decision or an effect.
      headline.textContent = beat.caption;
      detail.textContent = beat.detail;
    } else {
      headline.textContent = cue.headline;
      detail.textContent = cue.detail;
    }

    const canRoll = cue.canRoll && !rolling && beat === null;
    // The move is the player's to pick, and the cue has one to offer.
    const picksMove = cue.mine && (cue.kind === 'move' || cue.kind === 'move-again');
    const offers = picksMove && !rolling && beat === null;
    const pitches = suggestionStore.get();
    for (const { kind, button } of pitchButtons) {
      const move = pitches[kind];
      button.hidden = !(offers && move);
      if (move) button.dataset.tip = pitchTip(state, kind, move);
    }
    backButton.hidden = reviewing || rolling || beat !== null || !canTakeBack();
    dieButton.disabled = !canRoll;
    // The block pulses while the roll is the one thing the player can do. A roll for energy is a choice.
    bar.classList.toggle('cue--calls', canRoll && cue.kind === 'roll');
    dieButton.classList.toggle('cue__die--ready', canRoll);
    // The die has the color of the side that is about to roll. At rest it has no color.
    dieButton.classList.toggle('cue__die--gold', canRoll && state.turn === 'gold');
    dieButton.classList.toggle('cue__die--violet', canRoll && state.turn === 'violet');
    // While the die on the stage tumbles, this one is away. It comes back with the face up.
    die.show(canRoll ? 'ready' : tumbling ? 'away' : 'rest', state.lastRoll?.face ?? null);

    side.replaceChildren(
      ...(state.phase === 'over' ? [] : [createSideMark(state.turn), OWNER_NAMES[state.turn]]),
    );
    side.className = `cue__side cue__side--${state.turn}`;
  };

  draw();
  subscribeView(draw);
  stageStore.subscribe(draw);
  beatStore.subscribe(draw);
  suggestionStore.subscribe(draw);
  return bar;
};
